import { useState, useCallback } from 'react'
import { discoveryService } from '../services/discoveryService'

export const useDeviceDiscovery = () => {
  const [devices, setDevices] = useState([])
  const [isConnecting, setIsConnecting] = useState(false)
  const [connectionError, setConnectionError] = useState(null)

  // Connect to device manually by IP
  const connectToDevice = useCallback(async (ip, port = 80) => {
    try {
      setIsConnecting(true)
      setConnectionError(null)
      
      console.log(`🔗 Attempting to connect to ${ip}:${port}`)
      
      const device = await discoveryService.connectToDevice(ip, port)
      
      if (device) {
        setDevices(prev => {
          // Check if device already exists
          const exists = prev.find(d => d.ip === device.ip)
          if (exists) {
            // Update existing device
            return prev.map(d => d.ip === device.ip ? { ...d, ...device } : d)
          } else {
            // Add new device
            return [...prev, device]
          }
        })
        
        console.log(`✅ Successfully connected to device: ${device.name}`)
        return device
      }
      
    } catch (error) {
      setConnectionError(error.message)
      console.error('Failed to connect to device:', error)
      throw error
    } finally {
      setIsConnecting(false)
    }
  }, [])

  // Test connection to a device
  const testConnection = useCallback(async (ip, port = 80) => {
    try {
      setConnectionError(null)
      const status = await discoveryService.testConnection(ip, port)
      console.log('Connection test successful:', status)
      return status
    } catch (error) {
      setConnectionError(error.message)
      console.error('Connection test failed:', error)
      throw error
    }
  }, [])

  // Quick scan for common IPs (optional)
  const quickScan = useCallback(async () => {
    try {
      setIsConnecting(true)
      setConnectionError(null)
      
      console.log('🚀 Starting quick scan...')
      
      // Listen for discovered devices
      discoveryService.onDeviceFound((device) => {
        setDevices(prev => {
          const exists = prev.find(d => d.ip === device.ip)
          if (exists) {
            return prev.map(d => d.ip === device.ip ? { ...d, ...device } : d)
          } else {
            return [...prev, device]
          }
        })
      })
      
      // Listen for scan errors
      discoveryService.onError((error) => {
        setConnectionError(error.message)
        setIsConnecting(false)
      })
      
      const foundDevices = await discoveryService.quickScan()
      
      console.log(`📊 Quick scan completed: Found ${foundDevices.length} devices`)
      return foundDevices
      
    } catch (error) {
      setConnectionError(error.message)
      console.error('Quick scan failed:', error)
      throw error
    } finally {
      setIsConnecting(false)
    }
  }, [])

  // Get device status
  const getDeviceStatus = useCallback(async (ip, port = 80) => {
    try {
      setConnectionError(null)
      const status = await discoveryService.getDeviceStatus(ip, port)
      return status
    } catch (error) {
      setConnectionError(error.message)
      console.error('Failed to get device status:', error)
      throw error
    }
  }, [])

  // Clear devices list
  const clearDevices = useCallback(() => {
    setDevices([])
    setConnectionError(null)
  }, [])

  // Remove a device
  const removeDevice = useCallback((deviceIP) => {
    setDevices(prev => prev.filter(d => d.ip !== deviceIP))
  }, [])

  return {
    devices,
    isConnecting,
    connectionError,
    connectToDevice,
    testConnection,
    quickScan,
    getDeviceStatus,
    clearDevices,
    removeDevice
  }
} 